import { ENDPOINT } from '~/constants';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type BulkDismissParsingCasesPayload,
  type BulkDismissParsingCasesResponse,
} from './types';
import {
  zodBulkDismissParsingCasesPayloadSchema,
  zodBulkDismissParsingCasesResponseSchema,
} from './validations';

const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  403: () =>
    i18n.t(($) => $.bulkDismissParsingCases.error403, {
      ns: 'data',
    }),
  422: () =>
    i18n.t(($) => $.bulkDismissParsingCases.error422, {
      ns: 'data',
    }),
};

function getGenericErrorMessage() {
  return i18n.t(($) => $.bulkDismissParsingCases.error.generic, {
    ns: 'data',
  });
}

/**
 * Dismiss multiple parsing cases.
 *
 * @param payload - Case IDs to dismiss and an optional shared reason.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases
 */
export async function bulkDismissParsingCases(
  payload: BulkDismissParsingCasesPayload,
): Promise<BulkDismissParsingCasesResponse> {
  try {
    return await vestigasApi
      .post(ENDPOINT.PARSING_CASE.BULK_DISMISS(), {
        json: payload,
        validate: {
          body: zodBulkDismissParsingCasesPayloadSchema,
          response: zodBulkDismissParsingCasesResponseSchema,
        },
      })
      .json<BulkDismissParsingCasesResponse>();
  } catch (error) {
    log.error({
      message: 'Error bulk dismissing parsing cases',
      error,
    });

    const errorMessage =
      resolveMutationErrorMessage(
        error,
        ERROR_MESSAGE_BY_STATUS,
        getGenericErrorMessage,
      ) || getGenericErrorMessage();
    ToastService.error(errorMessage);

    throw error;
  }
}
